import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Alert,
  Box,
  CircularProgress,
  useTheme,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import TouchAppIcon from "@mui/icons-material/TouchApp";

import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import ConfigSwitchCard from "../../styled/ConfigSwitchCard";

// Define configuration options
const configOptions = [
  {
    id: "allow_cashier_general",
    title: "Caixa Geral",
    description: {
      enabled:
        "Caixa Geral Ativado: O caixa geral engloba todos os pedidos da sua operação (Delivery, Retirada e Salão)",
      disabled:
        "Caixa Geral Desativado: O caixa geral engloba todos os pedidos da sua operação (Delivery, Retirada e Salão)",
    },
    icon: TouchAppIcon,
    successMessage: (value) =>
      `Caixa Geral ${value ? "Ativado" : "Desativado"} com sucesso`,
    errorMessage: "Erro ao atualizar a disponibilidade do Caixa Geral",
    notificationId: "cashier-general-config",
  },
  {
    id: "allow_cashier_delivery",
    title: "Caixa Delivery",
    description: {
      enabled:
        "Caixa Delivery Ativado: O caixa delivery engloba todos os pedidos delivery da sua operação (iFood, Neemo, DDireto e Balcão)",
      disabled:
        "Caixa Delivery Desativado: O caixa delivery engloba todos os pedidos delivery da sua operação (iFood, Neemo, DDireto e Balcão)",
    },
    icon: TouchAppIcon,
    successMessage: (value) =>
      `Caixa Delivery ${value ? "Ativado" : "Desativado"} com sucesso`,
    errorMessage: "Erro ao atualizar a disponibilidade do Caixa Delivery",
    notificationId: "cashier-delivery-config",
  },
  {
    id: "allow_cashier_indoor",
    title: "Caixa Salão",
    description: {
      enabled:
        "Caixa Salão Ativado: O caixa salão engloba todos os pedidos em mesas da sua operação (iFood, Neemo, DDireto e Balcão)",
      disabled:
        "Caixa Salão Desativado: O caixa salão engloba todos os pedidos em mesas da sua operação (iFood, Neemo, DDireto e Balcão)",
    },
    icon: TouchAppIcon,
    successMessage: (value) =>
      `Caixa Salão ${value ? "Ativado" : "Desativado"} com sucesso`,
    errorMessage: "Erro ao atualizar a disponibilidade do Caixa Salão",
    notificationId: "cashier-indoor-config",
  },
];

export default function CashierTypeConfigsModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [configValues, setConfigValues] = useState({});

  useEffect(() => {
    if (!show) return;
    loadConfigs();
  }, [show]);

  const loadConfigs = async () => {
    try {
      setLoading(true);

      const configParams = configOptions.map((option) => option.id).join(",");
      const params = { config: configParams };
      const { data } = await getDeliveryCenterConfig(params);

      setConfigValues(data);
    } catch (err) {
      console.log(err);
      setErrorMessage(
        "Ocorreu um erro ao carregar as configurações. Tente novamente."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        "configs-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (configId, event) => {
    try {
      const newValue = event.target.checked;
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      if (!configOption) return;

      const data = {
        config: {
          [configId]: newValue,
        },
      };

      await setDeliveryCenterConfig(data);

      setConfigValues((prev) => ({
        ...prev,
        [configId]: newValue,
      }));

      dispatchSnackBarMessage(
        configOption.notificationId,
        configOption.successMessage(newValue),
        "success"
      );
    } catch (e) {
      console.error(e);
      const configOption = configOptions.find(
        (option) => option.id === configId
      );

      dispatchSnackBarMessage(
        `${configId}-update-error`,
        configOption?.errorMessage || "Erro ao atualizar configuração",
        "error"
      );
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="600px"
      maxWidth="90%"
    >
      <ModalHeader
        title="Tipos de Caixa"
        icon={DashboardIcon}
        onClose={closeModal}
      />

      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 4 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            configOptions.map((config) => {
              const isChecked = configValues[config.id] || false;

              return (
                <ConfigSwitchCard
                  key={config.id}
                  icon={config.icon}
                  title={config.title}
                  description={
                    isChecked
                      ? config.description.enabled
                      : config.description.disabled
                  }
                  isActive={isChecked}
                  onChange={(e) => handleConfigChange(config.id, e)}
                />
              );
            })
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
